import * as React from 'react';
import ExpiryDateInput from '../../../Components/ExpiryDateInput';
import Row from '../../../Components/Row';
import SelectCardType from '../../../Components/SelectCardType';
import TextInput from '../../../Components/TextInput';
import StringFormatter from '../../../Helpers/StringFormatter';
import Validator from '../../../Helpers/validator';
import SelectDateToDonate from '../../../Components/SelectDateToDonate';
import { CardPaymentDetails, ExpiryDate } from '../../../Types/Payments/PaymentDetails';
import StartDateInput from '../../../Components/StartDateInput';

interface Props {
    annualPaymentAmountPence: number;
    creditCardDetails: {
        cardType: string;
        cardHolderName: string;
        cardNumber: string;
        expDate: ExpiryDate;
        cvc: string;
        dateToDonateInMonth: string;
        startDonationFrom: ExpiryDate;
    };
    cardTypes: string[];

    buttonText?: string;
    buttonClass?: string;
    priceSymbol?: string;
    paymentSelection?: string;
    updateCreditCardDetails(mutate: (dd: CardPaymentDetails) => void): void;
    submit(): void;
}

export interface State {
    highlightErrors: boolean;

    startDateValid: number;
}

export default class CreditCardPaymentForm extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            highlightErrors: false,
            startDateValid: 0,
        };
    }

    handleSubmit = async () => {
        this.setState(state => ({ ...state, highlightErrors: true }));

        if (this.allValid()) {

            this.props.submit();
        }
    };

    allValid = () => {
        let t = this.props.creditCardDetails;
        const cardNumValid = Validator.isCardNumber(t.cardNumber);
        const cvcValid = Validator.isSecurityCode(t.cvc) || t.cvc === '';
        const nameValid = Validator.isNotEmpty(t.cardHolderName);
        const expValid = Validator.isExpDate(t.expDate);
        const cType = Validator.isNotEmpty(t.cardType);
        const sDateValid = this.props.paymentSelection !== 'monthly' ?
            true :
            Validator.isValidStartDateMonthAndYear(t.dateToDonateInMonth, t.startDonationFrom.month, t.startDonationFrom.year);
            
        return cardNumValid && cvcValid && nameValid && expValid && cType && sDateValid;
    }

    typeChanged = (cardType: string) => {
        this.props.updateCreditCardDetails(c => c.cardType = cardType);
    }

    cardNumberChanged = (cardNumber: string) => {
        this.props.updateCreditCardDetails(c => c.cardNumber = cardNumber);
    }

    cvcChanged = (cvc: string) => {
        this.props.updateCreditCardDetails(c => c.cvc = cvc);
    }

    expiryDateChanged = (expiryDate: ExpiryDate) => {
        this.props.updateCreditCardDetails(c => c.expDate = expiryDate);
    }

    cardHolderNameChanged = (cardHolderName: string) => {
        this.props.updateCreditCardDetails(c => c.cardHolderName = cardHolderName);
    }

    render() {

        let pSymbol = '';

        if (this.props.priceSymbol) {
            pSymbol = this.props.priceSymbol;
        }

        let submitBtnClass = 'btn less-padding ';

        if (this.props.buttonClass) {
            submitBtnClass += this.props.buttonClass;
        }

        return (
            <div>
                <Row>

                    {
                        this.props.cardTypes.length > 0 &&
                        <SelectCardType
                            key={'cardType'}
                            value={this.props.creditCardDetails.cardType}
                            onChange={this.typeChanged}

                            dataInvalidMessage={'Card Type is required.'}
                            forceHighlightErrors={this.state.highlightErrors}
                            label={'Card Type*'}
                            validator={Validator.isNotEmpty}
                            cardTypeList={this.props.cardTypes}
                        />
                    }

                    <TextInput
                        key={'cardNumber'}
                        id={'cardNumber'}
                        value={this.props.creditCardDetails.cardNumber}
                        onChange={this.cardNumberChanged}

                        dataInvalidMessage={'A valid Credit / Debit card is required.'}
                        forceHighlightErrors={this.state.highlightErrors}
                        label={'Card Number*'}
                        validator={Validator.isCardNumber}
                    />

                    <TextInput
                        key={'cvc'}
                        id={'cvc'}
                        value={this.props.creditCardDetails.cvc}
                        onChange={this.cvcChanged}

                        dataInvalidMessage={'Enter a valid CVC number, or leave blank.'}
                        forceHighlightErrors={this.state.highlightErrors}
                        label={'CVC (back of card)'}
                        validator={(s: string) => Validator.isSecurityCode(s) || s === ''}
                    />

                    <ExpiryDateInput
                        key={'expDate'}
                        label={'Expiry Date* (MM/YY)'}

                        value={this.props.creditCardDetails.expDate}

                        forceHighlightErrors={this.state.highlightErrors}

                        onChange={this.expiryDateChanged}
                    />

                    <TextInput
                        key={'cardHolderName'}
                        id={'cardHolderName'}
                        value={this.props.creditCardDetails.cardHolderName}
                        onChange={this.cardHolderNameChanged}

                        dataInvalidMessage={'Card holder name is required.'}
                        forceHighlightErrors={this.state.highlightErrors}
                        label={'Card Holder Name*'}
                        validator={Validator.isNotEmpty}
                    />

                    {this.props.paymentSelection === 'monthly' &&
                        <div>
                            <SelectDateToDonate
                                key={'dateToDonateInMonth'}
                                id={'dateToDonateInMonth'}
                                value={this.props.creditCardDetails.dateToDonateInMonth}
                                label={'Date to donate in month*'}
                                dataInvalidMessage={'Date to donate in month is required.'}
                                forceHighlightErrors={this.state.highlightErrors}

                                validator={Validator.isNotEmpty}

                                onChange={(date: string) => this.props.updateCreditCardDetails(d => (d.dateToDonateInMonth = date))}
                            />
                            <StartDateInput
                                id={'startDonationFrom'}
                                label={'Start donation* (MM/YY)'}
                                value={this.props.creditCardDetails.startDonationFrom}

                                forceHighlightErrors={this.state.highlightErrors}

                                onChange={(date: { month: string; year: string; isValid: number; }) => {
                                    this.setState({ startDateValid: date.isValid });
                                    this.props.updateCreditCardDetails(d => (d.startDonationFrom = date));
                                }}
                            />
                        </div>
                         
                    }
                </Row>

                <Row>

                    <div className="col s12 m7 lg7 xl7  sidePane">
                        <b>
                            You will be donating {' '} 
                            {pSymbol}
                            {StringFormatter.numberAsCurrency(this.props.annualPaymentAmountPence)}
                            {' '} {this.props.paymentSelection === 'monthly' ? 'per month.' : '.'}
                        </b>

                        <br />

                        You will be able to confirm the details on the next page.
                    </div>

                    <div className="submission-block col m5 s12 lg5  sidePane">
                        <a className={submitBtnClass} onClick={this.handleSubmit}>
                            {
                                this.props.buttonText
                                    ? this.props.buttonText
                                    : 'View Summary'
                            }
                        </a>
                    </div>

                </Row>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/MakeDonation/SignupJourney/PaymentForms/CreditCardPaymentForm.tsx